/**
 * Generate an RFC 4122 v4 UUID, falling back when `crypto.randomUUID` is
 * unavailable.
 *
 * `crypto.randomUUID` only exists in secure contexts and in browsers newer than
 * ~2021 (Chrome 92+, Safari 15.4+). Older mobile Chrome / Android WebViews call
 * it and throw `TypeError: crypto.randomUUID is not a function`, which crashed
 * `getDeviceId()` during auth-provider mount (Sentry SPEAK-CLIENT-CB). This
 * helper prefers the native implementation and only synthesizes a UUID when it
 * is missing.
 *
 * Pass `requireSecureRandom` for security-critical values (e.g. an OAuth nonce):
 * it forbids the Math.random fallback and throws if no secure RNG exists. The
 * default (false) keeps non-security ids (device id, element keys) generating.
 */
export function safeRandomUUID(requireSecureRandom = false): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }

  const bytes = new Uint8Array(16);
  if (typeof crypto !== "undefined" && typeof crypto.getRandomValues === "function") {
    crypto.getRandomValues(bytes);
  } else if (requireSecureRandom) {
    // Security-critical callers (the OAuth nonce in sso.ts) must never receive a
    // predictable value — fail loudly rather than fall back to Math.random.
    // Unreachable in practice: crypto.getRandomValues ships in every browser since ~2011.
    throw new Error("secure RNG unavailable");
  } else {
    // Non-security ids (device id, element keys) keep working without Web Crypto.
    for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256);
  }

  // Set version (4) and variant (10) bits per RFC 4122.
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;

  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0"));
  return `${hex[0]}${hex[1]}${hex[2]}${hex[3]}-${hex[4]}${hex[5]}-${hex[6]}${hex[7]}-${hex[8]}${hex[9]}-${hex[10]}${hex[11]}${hex[12]}${hex[13]}${hex[14]}${hex[15]}`;
}
